import React, { createContext, useContext, useState, useCallback } from 'react';
import api from '../services/api';

interface AuthState {
  token: string | null;
  userId: number | null;
  username: string | null;
  role: string | null;
  tenantId: number | null;
  isSuperadmin: boolean;
  mustChangePassword: boolean;
  tenantTimezone: string | null;
}

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  setPasswordChanged: () => void;
  setAutoLoginToken: (token: string, tenantId: number, timezone: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [auth, setAuth] = useState<AuthState>(() => {
    const stored = localStorage.getItem('auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      api.defaults.headers.common['Authorization'] = `Bearer ${parsed.token}`;
      return {
        token: parsed.token || null,
        userId: parsed.userId || null,
        username: parsed.username || null,
        role: parsed.role || null,
        tenantId: parsed.tenantId || null,
        isSuperadmin: parsed.isSuperadmin || false,
        mustChangePassword: parsed.mustChangePassword || false,
        tenantTimezone: parsed.tenantTimezone || null,
      };
    }
    return { token: null, userId: null, username: null, role: null, tenantId: null, isSuperadmin: false, mustChangePassword: false, tenantTimezone: null };
  });

  const login = useCallback(async (username: string, password: string) => {
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await api.post('/api/auth/login', formData, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    });

    const { access_token, user_id, username: uname, role: userRole, tenant_id, is_superadmin, must_change_password } = response.data;

    const newAuth: AuthState = {
      token: access_token,
      userId: user_id,
      username: uname,
      role: userRole,
      tenantId: tenant_id,
      isSuperadmin: is_superadmin,
      mustChangePassword: must_change_password,
      tenantTimezone: null,
    };

    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    localStorage.setItem('auth', JSON.stringify(newAuth));
    setAuth(newAuth);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('auth');
    delete api.defaults.headers.common['Authorization'];
    setAuth({ token: null, userId: null, username: null, role: null, tenantId: null, isSuperadmin: false, mustChangePassword: false, tenantTimezone: null });
  }, []);

  const setPasswordChanged = useCallback(() => {
    setAuth(prev => {
      const updated = { ...prev, mustChangePassword: false };
      localStorage.setItem('auth', JSON.stringify(updated));
      return updated;
    });
  }, []);

  const setAutoLoginToken = useCallback((token: string, tenantId: number, timezone: string) => {
    const updated: AuthState = {
      ...auth,
      token,
      tenantId,
      tenantTimezone: timezone,
    };
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    localStorage.setItem('auth', JSON.stringify(updated));
    setAuth(updated);
  }, [auth]);

  return (
    <AuthContext.Provider value={{ ...auth, login, logout, setPasswordChanged, setAutoLoginToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
